import React, {useMemo, useState, useRef, useEffect} from 'react'
import PropTypes from 'prop-types'
import {BodyCopy} from '@/components/ui/body-copy'
import {CircleCheck} from 'lucide-react'
import {goalPropType} from './utils'
import {Button as HecButton} from '@/components/ui/button'
import {Button} from '@/components/cta'
import {buttonFunctions} from '@/builder/blocks/cta'
import useLearningCenter from '~/app/hooks/use-learning-center'
import useLearningCenterMutation from 'hec/hooks/learning-center/useLearningCenterMutation'
import {useHistory} from 'react-router-dom'

const GoalResult = ({goal, isMobile, isGuest, 'data-testid': dataTestId}) => {
    const history = useHistory()
    const {getActiveGoal} = useLearningCenter()
    const {startGoal, openGoal} = useLearningCenterMutation()
    const [requestInProgress, setRequestInProgress] = useState(false)
    const [redirectToGoalPage, setRedirectToGoalPage] = useState(false)
    const activeGoal = getActiveGoal()

    const {id: goalId, title, description, benefits, dashboard} = goal

    const dashboardUrl = dashboard.value?.data?.url
    const benefitValues = useMemo(
        () => benefits.map(({benefit}) => benefit.value?.data?.benefit),
        [benefits]
    )

    const goalRef = useRef(null)

    const handleClick = async () => {
        const shouldStartGoal = !activeGoal
        setRequestInProgress(true)
        if (shouldStartGoal) {
            const response = await startGoal(goalId)
            if (!response.success) {
                throw new Error(response.errorMsg)
            }
        } else {
            const response = await openGoal(goalId)
            if (!response.success) {
                throw new Error(response.errorMsg)
            }
        }
        setRequestInProgress(false)

        history.push(dashboardUrl)
    }

    useEffect(() => {
        if (redirectToGoalPage) {
            handleClick()
        }
    }, [redirectToGoalPage]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isMobile && goalRef.current) {
            goalRef.current.scrollIntoView({behavior: 'smooth', block: 'center'})
        }
    }, [isMobile])

    const renderGoalAndDescription = () => (
        <div>
            <BodyCopy className="!font-medium text-accent pb-3" data-testid="goalResult-BodyCopy">
                Goal: {title}
            </BodyCopy>
            <BodyCopy data-testid="goalResult-BodyCopy">{description}</BodyCopy>
        </div>
    )

    const renderBenefits = () => {
        if (benefitValues.length > 0) {
            return (
                <div className="mt-8">
                    <BodyCopy
                        size="small"
                        className="!font-medium text-accent pb-1"
                        data-testid="goalResult-BodyCopy"
                    >
                        Benefits
                    </BodyCopy>
                    {benefitValues.map((benefit, i) => (
                        <div
                            key={`benefit-${i}`}
                            className="flex space-x-2 text-sm text-black py-1"
                        >
                            <CircleCheck
                                className="h-4 w-4 flex-shrink-0"
                                data-testid="goalResult-CircleCheck"
                            />
                            <span>{benefit}</span>
                        </div>
                    ))}
                </div>
            )
        }
    }

    const handleSignInClick = () => {
        // Open login drawer via hash navigation
        window.location.hash = 'login'
        // Set flag to redirect after login
        sessionStorage.setItem('redirectToGoalAfterLogin', 'true')
        sessionStorage.setItem('goalIdToStart', goalId)
        sessionStorage.setItem('goalDashboardUrl', dashboardUrl)
    }

    // Check if we need to redirect after login
    useEffect(() => {
        if (!isGuest) {
            const shouldRedirect = sessionStorage.getItem('redirectToGoalAfterLogin')
            const storedGoalId = sessionStorage.getItem('goalIdToStart')
            const storedDashboardUrl = sessionStorage.getItem('goalDashboardUrl')

            if (shouldRedirect === 'true' && storedGoalId === goalId) {
                sessionStorage.removeItem('redirectToGoalAfterLogin')
                sessionStorage.removeItem('goalIdToStart')
                sessionStorage.removeItem('goalDashboardUrl')
                setRedirectToGoalPage(true)
            }
        }
    }, [isGuest, goalId])

    const renderCTAButton = () =>
        isGuest ? (
            <Button
                ctaType="button"
                clickFunction={buttonFunctions.nothing}
                onClick={handleSignInClick}
                className="bg-accent px-6 py-4 mt-6 text-center hover:bg-accent"
                variant="primary"
                data-testid={dataTestId || 'goalResult-Button'}
            >
                Sign in to Learn More
            </Button>
        ) : (
            <HecButton
                ctaType="button"
                variant="primary"
                className="bg-accent px-6 py-4 mt-6 text-center hover:bg-accent"
                onClick={handleClick}
                disabled={requestInProgress}
                data-testid="goalResult-HecButton"
            >
                Start Learning
            </HecButton>
        )

    return isMobile ? (
        <div ref={goalRef} className="mx-4 mb-8 theme-medium">
            <div className="rounded-md p-4 bg-background">
                {renderGoalAndDescription()}
                {renderBenefits()}
            </div>
            {renderCTAButton()}
        </div>
    ) : (
        <div className="flex flex-col items-start p-10 theme-medium bg-background rounded-md">
            {renderGoalAndDescription()}
            {renderBenefits()}
            {renderCTAButton()}
        </div>
    )
}

export default GoalResult

GoalResult.propTypes = {
    goal: goalPropType,
    isMobile: PropTypes.bool.isRequired,
    isGuest: PropTypes.bool.isRequired,
    'data-testid': PropTypes.string
}
